import { queryOptions, useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, type MerchantScope, useScopedMerchantClient } from "./scoped-merchant-client";

export const useActiveJobs = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["jobs", scope.merchantId, "active"],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].jobs.active.$get({ param: scope.param })
        : await client.merchant[":merchantId"].jobs.active.$get({ param: scope.param });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

const jobQuery = (scope: MerchantScope, jobId: string) =>
  queryOptions({
    queryKey: ["jobs", scope.merchantId, jobId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].jobs[":jobId"].$get({
            param: { ...scope.param, jobId },
          })
        : await client.merchant[":merchantId"].jobs[":jobId"].$get({ param: { ...scope.param, jobId } });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });

export const useJob = (jobId: string) => useQuery(jobQuery(useScopedMerchantClient(), jobId));

/** The same query, for a callback that learns of a job from an event or a response. */
export const fetchJob = (scope: MerchantScope, jobId: string) => queryClient.fetchQuery(jobQuery(scope, jobId));

export const useJobFailures = (jobId: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["jobs", scope.merchantId, jobId, "failures"],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].jobs[":jobId"].failures.$get({
            param: { ...scope.param, jobId },
          })
        : await client.merchant[":merchantId"].jobs[":jobId"].failures.$get({ param: { ...scope.param, jobId } });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};
